// ============================================================
// TMR Portal Admin — Regras de Negócio (rota /regras-negocio): o padrão da TMR (25)
// ============================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useBusinessRules, useUpdateRuleDefault } from '../../data/businessRules/queries';
import {
  defaultOriginLabel,
  overrideCountLabel,
  ruleRangeLabel,
  ruleReach,
  ruleValueLabel,
} from '../../data/businessRules/status';
import type { BusinessRule } from '../../data/businessRules/contract';
import { RuleValueModal } from './RuleValueModal';

/**
 * The TMR's default for each business rule — what every Empresa without an exception sells with.
 *
 * The rules come from the backend's catalogue (code, not data), so the list is short and the same in every
 * environment; what the operator changes is the value. Each Empresa's exception lives on its own tab, like
 * the rates' template and an Empresa's rates, and each rule opens its own page with the Empresas that have
 * one (`25` §9).
 */
export function BusinessRulesPage() {
  const [editing, setEditing] = useState<BusinessRule | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const canRead = usePermission(PERMISSIONS.businessRulesGlobalRead);
  const canManage = usePermission(PERMISSIONS.businessRulesGlobalManage);

  const query = useBusinessRules(canRead);
  const update = useUpdateRuleDefault();

  // The route is already gated (Portal.tsx), so this is the operator who reached it by URL. Saying so beats
  // an error state that reads like a fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver as regras de negócio da TMR."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Plataforma"
        title="Regras de Negócio"
        desc="O padrão da TMR, que vale para toda empresa sem exceção própria."
      />

      {saved && <Notice tone="success">{saved}</Notice>}

      {query.isPending ? (
        <Skeleton rows={3} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <div className="pa-card">
          <EmptyState message="Nenhuma regra de negócio cadastrada." />
        </div>
      ) : (
        <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
          {query.data.items.map((rule) => (
            <BusinessRuleRow key={rule.key} rule={rule} canManage={canManage} onEdit={() => setEditing(rule)} />
          ))}
        </div>
      )}

      {editing && (
        <RuleValueModal
          title="Alterar o padrão"
          rule={editing}
          initialValue={editing.defaultValue}
          reach={ruleReach(editing, 'default')}
          confirmLabel="Salvar padrão"
          onSave={async (value) => {
            const rule = await update.mutateAsync({ key: editing.key, value });
            setSaved(`${rule.name}: o padrão agora é ${ruleValueLabel(rule, rule.defaultValue)}.`);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link BusinessRuleRow}. */
interface BusinessRuleRowProps {
  rule: BusinessRule;
  canManage: boolean;
  onEdit: () => void;
}

/**
 * One rule: what it is, the default in force, how far a change would reach, and who set it. The name opens
 * the rule's page, where the Empresas with an exception are listed.
 */
function BusinessRuleRow({ rule, canManage, onEdit }: BusinessRuleRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 20,
        padding: '18px 20px',
        borderBottom: '1px solid var(--border-default)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 360px', minWidth: 0 }}>
        <Link
          to={`/regras-negocio/${encodeURIComponent(rule.key)}`}
          style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink-900)', textDecoration: 'none' }}
        >
          {rule.name}
        </Link>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          {rule.description}
        </p>
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-subtle)' }}>
          {ruleRangeLabel(rule)} · {overrideCountLabel(rule.overrideCount)} · {defaultOriginLabel(rule)}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>
          {ruleValueLabel(rule, rule.defaultValue)}
        </span>
        {canManage && (
          <Btn variant="secondary" size="sm" icon={Icons.pen} onClick={onEdit}>
            Alterar padrão
          </Btn>
        )}
        <Link to={`/regras-negocio/${encodeURIComponent(rule.key)}`} className="pa-link-btn" style={{ fontSize: 13.5, textDecoration: 'none' }}>
          Ver empresas
        </Link>
      </div>
    </div>
  );
}
